import './style.css';
import { Game } from './core/game';
import { isEndless, isValidWorld } from './core/world';
import { writeSave } from './core/storage';
import { mountApp } from './ui/app';
import { toast } from './ui/toast';
import type { TelemetryDebugState } from './telemetry/api';
import { applyColumnScale } from './ui/columnScale';
import { installKeyboard } from './ui/keyboard';
import { setupPwa } from './pwa';
import { isMuted } from './ui/audioPref';
import { attachDamageHistory } from './ui/debug/history';
import { applyStoredOverrides } from './ui/debug/overrides';
import { enableSpeed, onSpeedChange, setSpeed, speedActive } from './ui/debug/speed';
import { CHANNEL, IS_PREPROD, debugEnabledByChannel } from './ui/channel';
import { APP_VERSION } from './ui/screens/devNotes';
import { loadSaveWithNotice, wasSaveReset } from './ui/saveNotice';
import { hasNodeDescriptions } from './ui/treeApi';
import { installEntitlement } from './ui/entitlement';
import { createStubBattleView } from './battle/stubView';
import type { BattleView, ViewKind } from './battle/view';

/**
 * Point d'entrée : charge la sauvegarde, crée `Game`, la vue de combat (3D
 * `src/game3d/battleView3d.ts`, ou la vue de secours sans WebGL2 — voir
 * `loadBattleView` ci-dessous) puis la coquille DOM. Gère l'autosave et la
 * pause `visibilitychange`.
 */

function createGame(): Game {
  try {
    // `loadSaveWithNotice` retient au passage si le cœur a jeté une sauvegarde
    // trop ancienne (save v2 — DECISIONS §E, A8) : l'écran titre l'annonce.
    const save = loadSaveWithNotice();
    return new Game(save ?? undefined);
  } catch {
    // Sauvegarde illisible ou `localStorage` indisponible : partie vierge.
    return new Game();
  }
}

/**
 * Charge la vue de combat (A58) : la vue 3D (`src/game3d/battleView3d.ts`,
 * chunk séparé par l'import dynamique) si WebGL2 est disponible, sinon la vue
 * de secours (`src/battle/stubView.ts`, charte F1). Un module 3D en erreur
 * retombe aussi sur la vue de secours, jamais sur une erreur fatale.
 *
 * Si le pipeline 3D échoue plus tard, à l'initialisation (nuanceur refusé par
 * un pilote), la vue 3D monte elle-même la vue de secours : `viewKind` suit
 * (`window.__dod.viewKind`, `renderInfo().fallback`).
 */
async function loadBattleView(game: Game): Promise<{ view: BattleView; viewKind: () => ViewKind }> {
  try {
    const mod3d = await import('./game3d/battleView3d');
    if (mod3d.supportsWebGL2()) {
      const view = mod3d.createBattleView3d(game, { onFirstThrow: () => {} });
      return { view, viewKind: () => (view.isFallback() ? 'stub' : '3d') };
    }
  } catch {
    /* module 3D en erreur : vue de secours */
  }
  return { view: createStubBattleView(game, { reason: 'WebGL2 indisponible' }), viewKind: () => 'stub' };
}

async function main(): Promise<void> {
  const root = document.querySelector<HTMLElement>('#app');
  if (!root) return;

  // Colonne centrée et échelle sur grand écran (phase 2, A78), souris et clavier :
  // posées avant la vue de combat, qui lit l'échelle à son montage.
  applyColumnScale();
  installKeyboard();

  const game = createGame();

  // ── Mode debug : surcharges persistées + historique des dégâts ──────
  // Décidé avant le montage : les surcharges d'équilibrage changent les stats
  // dérivées, et l'historique doit voir le tout premier lancer (COMMENTS §7).
  const debugEnabled = debugEnabledByChannel();
  // Vitesse de combat (A99) : relue avant le montage, la vue lit le facteur à
  // chaque image. Hors mode debug, toujours ×1 et vol normal.
  enableSpeed(debugEnabled);
  if (debugEnabled) {
    applyStoredOverrides(game);
    attachDamageHistory(game);
    // A87 : une vitesse ≠ ×1 ou « Sans vol de dés » exclut la partie des
    // classements — au choix du réglage, et à chaque entrée en combat (une
    // « Nouvelle partie » efface la marque, la préférence de l'installation reste).
    const markIfAccelerated = (): void => {
      if (speedActive()) game.markTainted();
    };
    onSpeedChange(markIfAccelerated);
    game.on('phase', ({ phase }) => {
      if (phase === 'expedition' || phase === 'invasion') markIfAccelerated();
    });
  }

  const { view: battle, viewKind } = await loadBattleView(game);
  battle.setMuted?.(isMuted());

  mountApp(root, game, battle);

  // ── Jeu complet (A112) : état de l'appareil, Google Play, codes ─────
  // La lecture locale est synchrone (le cœur connaît l'état avant la première
  // Accalmie) ; Google Play n'est interrogé qu'ensuite, en silence (TWA seulement).
  const entitlement = installEntitlement(game, { version: APP_VERSION, channel: CHANNEL });

  // ── Autosave (débouncée) ────────────────────────────────────────────
  let saveTimer: number | undefined;
  const scheduleSave = (): void => {
    if (saveTimer !== undefined) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      try {
        writeSave(game.toSave());
      } catch {
        /* stockage indisponible : tant pis, on continue sans persister */
      }
    }, 400);
  };
  game.on('phase', scheduleSave);
  game.on('purchase', scheduleSave);
  game.on('gold', scheduleSave);

  // ── Télémétrie d'équilibrage (phase 3, A78 à A80) ───────────────────
  // Installée **après** le montage, dans un `setTimeout` court : rien de
  // synchrone au lancement, jamais bloquante (A79). Le module est chargé à ce
  // moment-là (import dynamique), hors du chemin de démarrage.
  window.setTimeout(() => {
    void import('./telemetry')
      .then(({ installTelemetry }) => {
        // Avis à l'écran (A85, v0.40.4) : en préprod ou en mode debug, un envoi en
        // erreur est annoncé par un avis éphémère avec la raison du Worker, et le
        // rétablissement aussi ; en production, rien (A80 : sans annonce), l'état
        // reste lisible dans le menu Réglages de l'Accalmie.
        let lastWasError = false;
        const onResult = (st: TelemetryDebugState): void => {
          const r = st.lastResult ?? '';
          const isError = r.startsWith('error');
          if (IS_PREPROD || debugEnabled) {
            if (isError) toast(`Télémétrie : envoi en échec (${r}${st.lastDetail ? ` — ${st.lastDetail}` : ''})`, 7000);
            else if (lastWasError && r === 'ok') toast(`Télémétrie : envoi rétabli (${st.sent} enregistrement${st.sent > 1 ? 's' : ''} envoyé${st.sent > 1 ? 's' : ''})`, 4000);
          }
          lastWasError = isError;
          // L'Accalmie affiche un bandeau tant que le dernier envoi est en échec (A85).
          document.dispatchEvent(new CustomEvent('dod:telemetry-result'));
        };
        const api = installTelemetry(game, battle, { version: APP_VERSION, channel: CHANNEL, viewKind, onResult });
        if (debugEnabled) {
          const dod = (window as unknown as { __dod?: Record<string, unknown> }).__dod;
          if (dod) {
            dod.telemetry = {
              state: () => api.state(),
              flush: () => api.flush(),
              lastRecord: () => api.lastRecord(),
            };
          }
        }
      })
      .catch(() => {
        /* télémétrie indisponible : le jeu n'en dépend en rien */
      });
  }, 250);

  // ── PWA (A44) : service worker et avis de nouvelle version ──────────
  // Production seulement (`src/pwa.ts`). Avant de recharger sur la nouvelle
  // version, la partie est sauvegardée sans attendre l'autosave débouncée.
  setupPwa(game, () => {
    if (saveTimer !== undefined) window.clearTimeout(saveTimer);
    writeSave(game.toSave());
  });

  // Aucune boucle de chronos indépendante ici : la vue de combat (réelle ou
  // stub) fait avancer `game.tick()` elle-même pendant qu'elle est démarrée
  // (`battle.start()`/`stop()`, pilotés par `src/ui/app.ts` selon la phase).
  // Un second appelant de `tick()` ferait avancer les chronos deux fois plus
  // vite pendant le combat — voir docs/DECISIONS.md §D (UI).

  // ── Pause / reprise ─────────────────────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      battle.stop();
    } else if (game.state.phase === 'expedition' || game.state.phase === 'invasion') {
      battle.start();
    }
  });

  // ── Débogage e2e : exposé seulement en dev ou avec ?debug ───────────
  if (debugEnabled) {
    /**
     * Triche de test (jamais dans un build de prod sans `?debug`) : crédite de
     * l'or pour monter une situation de jeu en quelques appels au lieu de
     * rejouer des dizaines de jours. On écrit dans `state` (exposé en lecture
     * seule) puis on rediffuse l'événement `gold` pour que les écrans se
     * rafraîchissent — c'est le seul endroit du code qui s'autorise cette
     * entorse à DECISIONS D9, et il est hors du chemin de production.
     */
    // A87 : les triches de la console marquent la partie (exclue des classements).
    const cheat = {
      /**
       * Vitesse de combat (A99) : pose le facteur (1, 2, 4 ou 8 ; toute autre
       * valeur donne 1) et renvoie la valeur effective. Un facteur ≠ 1 marque la
       * partie (`onSpeedChange` ci-dessus).
       */
      speed(n: number): number {
        return setSpeed(n);
      },
      gold(n: number): number {
        game.markTainted();
        const mutable = game.state as { gold: number };
        mutable.gold = Math.max(0, Math.round(n));
        (game as unknown as { emit: (ev: 'gold', p: { gold: number }) => void }).emit('gold', {
          gold: mutable.gold,
        });
        return mutable.gold;
      },
      /**
       * Jeu complet (A112) : `entitle(true)` ouvre l'appareil comme un achat
       * (source `debug`, `claimGate()` si le niveau 3 est dû), `entitle(false)`
       * le referme. **Marque la partie** (exclue des classements), à la
       * différence de `unlock()` / `lock()` du cœur. Renvoie l'état de l'appareil.
       */
      entitle(on = true): boolean {
        game.markTainted();
        if (on) entitlement.unlockWith('debug');
        else entitlement.revokeEntitlement();
        return entitlement.current() !== null;
      },
      /**
       * Pose le niveau `n` (jour 1) : monde et boss du mode infini tirés par
       * `ensureWorld` au-delà du niveau 10, effacés en campagne ; la phase est
       * rediffusée pour que l'écran courant se redessine (phase 5).
       *
       * A94 (v0.46.0) : porte aussi `stats.bestLevel` à `max(bestLevel, n)` — les
       * portes de l'Abîme s'ouvrent sur ce meilleur niveau — ; dès `n ≥ 11` la
       * victoire est tenue pour vue (`creditsSeen`) et l'Artisan des dés est
       * offert (`game.grantArtisan()`, quand le cœur l'expose), comme à
       * l'entrée en mode infini.
       */
      level(n: number): number {
        game.markTainted();
        const mutable = game.state as {
          level: number;
          day: number;
          world: unknown;
          bossId: unknown;
          stats: { bestLevel: number; creditsSeen: boolean };
        };
        mutable.level = Math.max(1, Math.round(n));
        mutable.day = 1;
        mutable.world = null;
        mutable.bossId = null;
        mutable.stats.bestLevel = Math.max(mutable.stats.bestLevel, mutable.level);
        if (mutable.level >= 11) {
          mutable.stats.creditsSeen = true;
          (game as unknown as { grantArtisan?: () => boolean }).grantArtisan?.();
        }
        game.ensureWorld();
        (game as unknown as { emit: (ev: 'phase', p: { phase: string }) => void }).emit('phase', {
          phase: game.state.phase,
        });
        return mutable.level;
      },
      /**
       * Triche des e2e de l'Abîme (A94) : `level(n)` — meilleur niveau, Artisan
       * des dés offert à partir de 11 — puis rediffusion de la phase, pour que
       * l'écran courant (couloir de l'Abîme, onglet Forge, Corrupted GachaDice)
       * se redessine.
       */
      abyss(level: number): number {
        const n = cheat.level(level);
        (game as unknown as { emit: (ev: 'phase', p: { phase: string }) => void }).emit('phase', {
          phase: game.state.phase,
        });
        return n;
      },
      /**
       * Monde du prochain combat infini (v0.40.0) : sans argument, retiré par
       * la règle du monde par combat (`game.nextWorld()`) ; avec un monde
       * valide (`{ kind: 'zone', zone }` ou `{ kind: 'procedural', seed }`),
       * posé tel quel. Sans effet en campagne. La phase est rediffusée pour
       * que l'en-tête de l'Accalmie se redessine ; renvoie le monde courant.
       */
      nextWorld(world?: unknown): unknown {
        if (world === undefined) game.nextWorld();
        else if (isEndless(game.state.level) && isValidWorld(world)) {
          (game.state as { world: unknown }).world = { ...world };
        }
        (game as unknown as { emit: (ev: 'phase', p: { phase: string }) => void }).emit('phase', {
          phase: game.state.phase,
        });
        return game.state.world ? { ...game.state.world } : null;
      },
      /**
       * Mur payant (v0.55.0, A112) : tient le jeu pour acheté
       * (`game.setEntitled(true)`) puis réclame le niveau dû
       * (`game.claimGate()`), dont le résultat est renvoyé. Ne marque pas la
       * partie : c'est l'interface qui décide du marquage de sa propre
       * commande de debug.
       */
      unlock(): boolean {
        game.setEntitled(true);
        return game.claimGate();
      },
      /** Mur payant (A112) : referme le jeu (`game.setEntitled(false)`) ; renvoie `true`. */
      lock(): boolean {
        game.setEntitled(false);
        return true;
      },
      /**
       * Abat l'ennemi courant par le chemin normal (lancer tiré puis appliqué) :
       * ses PV tombent à 1, sa résurrection est consommée et son effet retiré
       * (esquive, emplacements bloqués…), si bien que le lancer suivant le tue
       * — un boss termine donc l'Invasion (phase 5).
       */
      killEnemy(): boolean {
        game.markTainted();
        for (let i = 0; i < 20; i++) {
          const s = game.state as {
            phase: string;
            enemy: { hp: number; revived: boolean; effect: unknown; shield: boolean } | null;
            cooldownRemaining: number;
            stunRemaining: number;
            spawnDelay: number;
          };
          const enemy = s.enemy;
          if (!enemy || (s.phase !== 'expedition' && s.phase !== 'invasion')) return false;
          enemy.hp = Math.min(enemy.hp, 1);
          enemy.revived = true;
          enemy.effect = null;
          enemy.shield = false;
          s.cooldownRemaining = 0;
          s.stunRemaining = 0;
          s.spawnDelay = 0;
          const r = game.rollThrow({ byMinion: game.minionActive() });
          if (r.id >= 0 && game.applyThrow(r).killed) return true;
        }
        return false;
      },
    };
    /**
     * Drapeaux d'interface exposés aux e2e : ils disent ce que le cœur a
     * réellement livré (info-bulles de nœuds, sauvegarde réinitialisée), pour
     * que les specs vérifient le bon comportement au lieu de le deviner.
     */
    const ui = {
      hasNodeDescriptions: (): boolean => hasNodeDescriptions(game),
      saveWasReset: (): boolean => wasSaveReset(),
    };
    (
      window as unknown as {
        __dod: { game: Game; cheat: typeof cheat; ui: typeof ui; readonly viewKind: ViewKind };
      }
    ).__dod = {
      game,
      cheat,
      ui,
      // Lu à la demande : la vue 3D peut basculer sur la vue de secours.
      get viewKind(): ViewKind {
        return viewKind();
      },
    };
  }
}

/**
 * Écran de démarrage (`index.html`, `.boot`) : si `main()` échoue avant le
 * montage de l'application, « Chargement… » resterait affiché pour toujours
 * (v0.40.2, retour « ça charge sans fin au démarrage »). On remplace le texte
 * par la raison et deux issues : recharger, ou repartir d'une sauvegarde vide
 * (après confirmation ; l'ancienne est gardée sous une autre clé).
 */
function showBootError(err: unknown): void {
  const boot = document.querySelector<HTMLElement>('.boot');
  if (!boot) return;
  const message = err instanceof Error ? `${err.name} : ${err.message}` : String(err);
  const text = boot.querySelector<HTMLElement>('.boot-text');
  if (text) {
    text.textContent = `Le jeu n'a pas pu démarrer. ${message}`;
    text.style.animation = 'none';
  }
  const actions = document.createElement('p');
  actions.className = 'boot-actions';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Recharger';
  reload.addEventListener('click', () => window.location.reload());
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.textContent = 'Repartir sans la sauvegarde';
  reset.addEventListener('click', () => {
    if (!window.confirm('Mettre la sauvegarde de côté et redémarrer le jeu ? (elle est conservée sous la clé diceordie.save.v1.backup)')) return;
    try {
      const raw = localStorage.getItem('diceordie.save.v1');
      if (raw !== null) localStorage.setItem('diceordie.save.v1.backup', raw);
      localStorage.removeItem('diceordie.save.v1');
    } catch {
      /* stockage inaccessible : on recharge quand même */
    }
    window.location.reload();
  });
  actions.append(reload, ' ', reset);
  boot.appendChild(actions);
  console.error('[DiceOrDie] démarrage impossible', err);
}

main().catch(showBootError);
